import { ReactNode, useId, useLayoutEffect, useRef, useState, HTMLAttributes, RefObject } from 'react';
import { createPortal } from 'react-dom';

interface Position { top: number; left: number; above: boolean }

/**
 * Hover/focus tooltip state for any trigger element. The tooltip renders in a
 * portal so it is never clipped by a scrolling table or card, and the trigger
 * points at it with aria-describedby.
 */
export function useTooltip<T extends HTMLElement>(content: ReactNode): {
  ref: RefObject<T>;
  triggerProps: HTMLAttributes<T>;
  tooltip: ReactNode;
} {
  const id = useId();
  const ref = useRef<T>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Position | null>(null);

  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const above = r.top > 120;
    setPos({
      top: above ? r.top - 8 : r.bottom + 8,
      left: Math.min(Math.max(r.left + r.width / 2, 140), window.innerWidth - 140),
      above,
    });
  }, [open]);

  const triggerProps: HTMLAttributes<T> = {
    'aria-describedby': open ? id : undefined,
    onMouseEnter: () => setOpen(true),
    onMouseLeave: () => setOpen(false),
    onFocus: () => setOpen(true),
    onBlur: () => setOpen(false),
    onKeyDown: (e) => { if (e.key === 'Escape') setOpen(false); },
  };

  const tooltip = open && pos && content ? createPortal(
    <div
      role="tooltip"
      id={id}
      style={{ position: 'fixed', top: pos.top, left: pos.left, transform: pos.above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)' }}
      className="pointer-events-none z-[9999] w-64 max-w-[calc(100vw-2rem)] rounded-[10px] border border-strokedark bg-boxdark px-3 py-2 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-white shadow-card">
      {content}
    </div>,
    document.body,
  ) : null;

  return { ref, triggerProps, tooltip };
}

interface Props {
  /** The explanation shown on hover or keyboard focus. */
  content: ReactNode;
  children: ReactNode;
  /** Extra classes on the trigger span. */
  className?: string;
}

/** A focusable inline trigger with a tooltip. Do not nest inside a button; use useTooltip there. */
export default function Tooltip({ content, children, className = '' }: Props) {
  const { ref, triggerProps, tooltip } = useTooltip<HTMLSpanElement>(content);
  return (
    <>
      <span ref={ref} tabIndex={0} {...triggerProps}
        className={`inline cursor-help rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary ${className}`}>
        {children}
      </span>
      {tooltip}
    </>
  );
}
